import type { LegalDocument } from './types'

export const recordingAndDataRetention: LegalDocument = {
  path: '/recording-and-data-retention',
  title: 'Recording & Data Retention',
  blocks: [
    { type: 'title', text: 'Recording and Data Retention Policy' },
    {
      type: 'paragraph',
      text: 'MissFit Coaching™ · Executive & Professional Job-Search Coaching',
    },
    {
      type: 'paragraph',
      text: 'Version 1.0 · Effective September 19, 2026 · Referred to in Sections 8.5 and 16 of the Client Engagement Agreement',
    },
    { type: 'callout', text: 'ATTORNEY REVIEWED AND APPROVED — CLEARED FOR USE' },
    {
      type: 'paragraph',
      text: 'This is the policy named in your Client Engagement Agreement. It sets out how MissFit records coaching sessions, how long it keeps each kind of record, and how records are disposed of. It is published so that you can read it before you sign and refer to it at any time afterward.',
    },
    {
      type: 'paragraph',
      text: [
        {
          text: 'If this policy and your Agreement ever disagree, your Agreement controls.',
          bold: true,
        },
        {
          text: ' This policy adds operational detail; it cannot reduce any commitment made to you in the Agreement.',
        },
      ],
    },

    { type: 'heading', text: '1. Recording' },
    {
      type: 'paragraph',
      text: [
        { text: 'Why we record.', bold: true },
        {
          text: ' Coaching sessions and consultations are recorded and transcribed so your coach can be fully present with you rather than taking notes, prepare accurate follow-up and career documents, support internal quality review and coach development, and maintain business records.',
        },
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'How we record.', bold: true },
        {
          text: " Recording is performed through the video-conferencing platform used for your sessions. Transcripts are produced by that platform's built-in transcript feature, which labels speakers using participant account information. MissFit does not use third-party AI notetaking services and does not permit outside notetaking tools to join your sessions. Any analysis MissFit performs is performed on the written transcript, not on the audio.",
        },
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'Your right to decline.', bold: true },
        {
          text: ' You may decline recording — for all sessions or for any single session — by telling us before the session begins. We will disable recording and use written notes instead. Declining does not affect your services, their scope, their pace, or your Agreement in any way. Your consent and any declination are logged, and a declination carries forward to rescheduled sessions unless you change it.',
        },
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'Recording by you.', bold: true },
        {
          text: ' Illinois requires the consent of every party to record a private conversation. You agree not to record any session, call, or consultation without telling MissFit before recording begins.',
        },
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'Sessions before you signed.', bold: true },
        {
          text: ' A consultation recorded before your Agreement was signed was recorded under the consent you gave when booking it. Your Agreement governs that recording from its date forward.',
        },
      ],
    },

    { type: 'heading', text: '2. Who can see your records' },
    {
      type: 'paragraph',
      text: 'Access is limited to the MissFit team members assigned to your engagement, and to the automation that maintains your tracking records. Every team member is bound by a written confidentiality agreement. No client data is kept on personal devices or in personal accounts.',
    },
    {
      type: 'paragraph',
      text: 'We use reputable service providers — cloud storage and document platforms, scheduling tools, video conferencing, and AI-assisted research and analysis tools — and share with them only what the work requires, under provider terms that commit to confidentiality and prohibit the provider from using your information for its own purposes. We do not sell your personal information and do not share it with third parties for their marketing.',
    },

    { type: 'heading', text: '3. How long we keep each record' },
    {
      type: 'paragraph',
      text: 'These periods match Section 8.3 of your Agreement. Where this table lists a record type the Agreement does not name, the period is the one required by law or by ordinary business practice.',
    },
    {
      type: 'table',
      head: ['Record', 'While your engagement is active', 'After it ends'],
      rows: [
        [
          'Audio-video session recordings',
          'Retained',
          '24 months from the session date, or the end of your engagement, whichever is later — then permanently deleted',
        ],
        [
          'Session transcripts',
          'Retained',
          '4 years — then deleted, or de-identified where that can genuinely be achieved',
        ],
        [
          'Career documents and work product — resumes, North Star, Momentum Maximizer™ workbook, Bridge™ list',
          'Retained',
          '4 years — then permanently deleted. Returned to you within five business days of your end date in every case',
        ],
        [
          'Forwarded job-search email in our tracking mailbox',
          'Retained, labeled to you',
          '30 days after your engagement ends or forwarding stops — then permanently deleted',
        ],
        [
          'Prospect records where no engagement began',
          'Not applicable',
          '6 months from last contact — then deleted',
        ],
        [
          'Signed agreements, consents, and authorizations',
          'Retained',
          '7 years — retained as proof of what permission was given and when',
        ],
        ['Invoices and payment records', 'Retained', '7 years, as tax law requires'],
        [
          'Published testimonials and their signed releases',
          'Retained',
          'As long as the testimonial is in use, plus 3 years',
        ],
        [
          'De-identified research records, where you separately consented',
          'Retained',
          'Indefinitely, in de-identified form',
        ],
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'Why recordings are kept at least 24 months.', bold: true },
        {
          text: ' Executive searches commonly run six to eighteen months, and earlier sessions remain relevant working context throughout. The whichever-is-later rule ensures no recording is deleted while your engagement is still active.',
        },
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'A note on de-identifying transcripts.', bold: true },
        {
          text: ' Senior careers are distinctive, and some coaching narratives cannot be de-identified no matter how carefully names are removed. Where that is the case, the transcript is deleted rather than kept. Deletion is the default; de-identification is the exception that must be earned.',
        },
      ],
    },
    {
      type: 'paragraph',
      text: [
        { text: 'Two standard exceptions.', bold: true },
        {
          text: ' Records subject to a legal hold, or that we must keep by law, are retained for the required period regardless of this schedule. Every exception is logged with its reason and date. Deletion removes records from active systems; residual copies in routine, access-restricted backups are purged on the normal backup cycle and are not used for any other purpose in the interim.',
        },
      ],
    },

    { type: 'heading', text: '4. How records are disposed of' },
    {
      type: 'bullets',
      items: [
        'Email is deleted from the tracking mailbox and the trash is emptied, so the deletion is permanent.',
        'Files are deleted and purged from trash, and your folders are removed from shared-drive permissions.',
        'Nothing is held on personal devices or personal accounts, so there is nothing to dispose of there.',
      ],
    },

    { type: 'heading', text: '5. How we make sure this actually happens' },
    {
      type: 'bullets',
      items: [
        [
          { text: 'Monthly sweep, first business day.', bold: true },
          {
            text: ' Every engagement that ended 30 or more days ago is checked, forwarded-email copies are deleted, the deletion is logged, and you receive a confirmation note.',
          },
        ],
        [
          { text: 'Annual sweep.', bold: true },
          {
            text: ' Records that have reached the end of their period under Section 3 are deleted and the deletion is logged.',
          },
        ],
        [
          { text: 'Quarterly review.', bold: true },
          { text: ' One closed engagement is spot-checked for residual data.' },
        ],
        [
          { text: 'Annual policy review.', bold: true },
          { text: ' This policy is reviewed once a year and reissued if anything changes.' },
        ],
      ],
    },

    { type: 'heading', text: '6. Your rights' },
    {
      type: 'paragraph',
      text: 'You may ask for a copy of the personal information we hold about you, ask us to correct it, or ask us to delete it — except records we must keep by law or hold for a legal proceeding — by emailing dee@missfitcoaching.com. We respond within a reasonable time and complete deletion requests within 30 days from active systems, with residual backup copies purged on the normal backup cycle.',
    },
    {
      type: 'paragraph',
      text: [
        { text: 'What a deletion request reaches.', bold: true },
        {
          text: ' Your recordings, transcripts, career documents, work product, and forwarded email copies. It does not reach information already de-identified, because such records contain nothing linking them to you and cannot be located. It does not reach findings already published, though we will not republish material with your information included. Consent records are kept even after the underlying material is deleted, as proof of what permission was given.',
        },
      ],
    },

    { type: 'heading', text: '7. If something goes wrong' },
    {
      type: 'paragraph',
      text: 'If a data breach affecting your personal information occurs, we will notify you and any required authorities without unreasonable delay, consistent with the Illinois Personal Information Protection Act (815 ILCS 530/). Any suspected exposure is escalated to Dee DeRidder immediately.',
    },

    { type: 'heading', text: '8. Contact' },
    {
      type: 'paragraph',
      text: 'Dee Dee Holdings LLC, doing business as MissFit Coaching™. Questions about this policy, or any request under Section 6, go to dee@missfitcoaching.com.',
    },
    {
      type: 'paragraph',
      text: [{ text: 'Reviewed annually. Next review: September 2027.', italic: true }],
    },
  ],
}
